import { isSuccess } from '@/context/services/type';
import { isCommonError } from '@/libs/typeguard/isError';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetAiAgentSettingQueryKey,
  UpdateAiAgentServiceSettingInput,
  useGetAiAgentSettingSuspense,
  useReceivedInvoicesSettings,
  useUpdateAiAgentServiceSettings,
  useUpdateAiAgentSetting,
} from 'ap-openapi';
import { useCallback, useMemo } from 'react';
import {
  UpdateAiAgentMainSettingResult,
  UpdateAiAgentServicesSettingResult,
} from './type';

const getErrorMessages = (data: unknown): string[] | undefined => {
  if (data !== null && data !== undefined && typeof data === 'object') {
    const dataRecord: Record<string, unknown> = { ...data };
    if (isCommonError(dataRecord)) {
      return Array.isArray(dataRecord.messages)
        ? dataRecord.messages
        : undefined;
    }
  }
  return undefined;
};

export const useGetReceivedInvoiceSettingsService = (enabled = true) => {
  const { data, isLoading } = useReceivedInvoicesSettings({
    query: { enabled, staleTime: 0 },
  });
  return useMemo(
    () => ({
      isPaymentRequestSupportAgentEnabled:
        data?.data?.payment_request_support_agent?.is_enabled ?? false,
      isLoading,
    }),
    [data, isLoading]
  );
};

export const useGetAiAgentsSettingService = () => {
  const { data, error, isLoading, refetch } = useGetAiAgentSettingSuspense();
  if (error) throw error;
  return useMemo(
    () => ({
      data: data?.data,
      isLoading,
      refetch,
    }),
    [data, isLoading, refetch]
  );
};

export const useUpdateAiAgentMainSetting = () => {
  const { mutateAsync: _mutateAsync } = useUpdateAiAgentSetting();
  const queryClient = useQueryClient();

  const mutateAsync = useCallback(
    async (isEnabled: boolean): Promise<UpdateAiAgentMainSettingResult> => {
      const resp = await _mutateAsync({
        data: {
          is_ai_agent_enabled: isEnabled,
        },
      });

      if (isSuccess(resp.status)) {
        await queryClient.invalidateQueries({
          queryKey: getGetAiAgentSettingQueryKey(),
        });
        return { status: 'success' };
      } else {
        const errorMessages = getErrorMessages(resp.data);
        return {
          status: 'error',
          statusCode: resp.status,
          errorMessages,
        };
      }
    },
    [_mutateAsync, queryClient]
  );

  return { mutateAsync };
};

export const useUpdateAiAgentServicesSetting = () => {
  const { mutateAsync: _mutateAsync } = useUpdateAiAgentServiceSettings();
  const queryClient = useQueryClient();
  const mutateAsync = useCallback(
    async (
      data: UpdateAiAgentServiceSettingInput[]
    ): Promise<UpdateAiAgentServicesSettingResult> => {
      const resp = await _mutateAsync({
        data: {
          ai_agent_service_settings: data,
        },
      });

      if (
        isSuccess(resp.status) &&
        resp.data?.results?.some((result) => result.status === 'success')
      ) {
        await queryClient.invalidateQueries({
          queryKey: getGetAiAgentSettingQueryKey(),
        });
        return {
          results: resp.data?.results ?? [],
          status: 'success',
        };
      } else {
        // Extract error messages from API response
        const errorMessages = getErrorMessages(resp.data);
        return {
          results: [],
          status: 'error',
          statusCode: resp.status,
          errorMessages,
        };
      }
    },
    [_mutateAsync, queryClient]
  );

  return { mutateAsync };
};
